import { useTranslation } from "react-i18next"
import { pushDialog, popDialog } from "@/store/dialogs-state"
import { projectAtom } from "@/store/project/projects-state"
import { PixiesetService } from "@/store/Pixieset/PixiesetService"
import { useAtomValue, useAtom } from "jotai"
import { Dialog } from "../ui/Dialog"
import { InputDialog } from "./InputDialog"
import { CollectionResponseDTO } from "@/api/PSApiOperations"
import { formattedPSImagePathToUrl } from "@/store/Pixieset/pixieset-image-urls"
import clsx from "clsx"
import { Spinner } from "../ui/Spinner"
import { Icon } from "../icons/Icon"
import { format, isValid, parseISO } from "date-fns"
import { toast } from "react-hot-toast"
import { ConfirmDialog } from "./ConfirmDialog"
import { pixiesetURLs } from "@/utils/pixieset-urls"
import { registerUserRefreshOnTabReturn } from "@/api/users-api"
import { getBackendErrorMessage, isAccountDelinquentError } from "@/utils/error"
import { useEffect, useRef, useState } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { isAuthReadyAtom } from "@/store/auth-state"
import { useDebouncedValue } from "@/utils/useDebounce"
import { SearchField } from "../ui/SearchField"
import { Button } from "../ui/Button"

export const SelectCGCollectionDialog = (props: {
	onSelect: (collection: CollectionResponseDTO) => void
}) => {
	const { t } = useTranslation("components")
	const [project] = useAtom(projectAtom)
	const [searchTerm, setSearchTerm] = useState("")

	const showCreateCollectionDialog = () => {
		if (!project) return
		pushDialog(InputDialog, {
			title: t("selectCGCollectionDialog.createCollectionTitle"),
			buttonTitle: "Create",
			initialInput: project.name,
			trackingViewName: "create_collection_dialog",
			confirmWithEnter: true,
			onConfirm: async (name: string) => {
				popDialog(2)
				try {
					const collection = await PixiesetService.createCollection(name, project)
					props.onSelect(collection)
				} catch (error) {
					console.error("Failed to create collection:", error)

					if (isAccountDelinquentError(error)) {
						pushDialog(ConfirmDialog, {
							title: t("selectCGCollectionDialog.failedToCreateTitle"),
							message:
								getBackendErrorMessage(error) ||
								t("selectCGCollectionDialog.delinquentAccount"),
							secondary: { title: t("common:action.cancel") },
							primary: {
								title: t("selectCGCollectionDialog.manageBilling"),
								handler: () => {
									registerUserRefreshOnTabReturn(
										"pixieset-manage-delinquent-return",
									)
									window.open(
										pixiesetURLs.gallery.accountBilling,
										"_blank",
										"noopener,noreferrer",
									)
								},
							},
							trackingViewName: "delinquent_warning",
						})
					} else {
						toast.error(t("selectCGCollectionDialog.failedToCreateCollectionPlease"))
					}
				}
			},
		})
	}
	return (
		<Dialog
			contentSize="md"
			canGoBack
			trackingViewName="select_cg_collection_dialog"
			headerContent={
				<SearchField
					value={searchTerm}
					onChange={setSearchTerm}
					placeholder={t("selectCGCollectionDialog.searchCollections")}
					aria-label={t("selectCGCollectionDialog.searchCollections")}
					className="w-72 max-w-full"
					data-disable-enter-key="true"
				/>
			}
		>
			<SelectCGCollectionView
				onSelect={props.onSelect}
				mode="select"
				onAddNew={showCreateCollectionDialog}
				searchTerm={searchTerm}
			/>
		</Dialog>
	)
}

export const SelectCGCollectionView = (props: {
	mode: "confirm" | "select"
	onSelect: (collection: CollectionResponseDTO) => void
	onAddNew?: () => void
	selectedCollection?: CollectionResponseDTO
	preselectedCollection?: CollectionResponseDTO
	searchTerm?: string
}) => {
	const { t } = useTranslation("components")
	const isAuthReady = useAtomValue(isAuthReadyAtom)
	const preselectedCollection = useRef(props.preselectedCollection).current
	const normalizedSearchTerm = props.searchTerm?.trim() ?? ""
	const debouncedSearchTerm = useDebouncedValue(normalizedSearchTerm, 200)
	const searchTerm = normalizedSearchTerm ? debouncedSearchTerm : ""
	const isDebouncing = normalizedSearchTerm !== searchTerm
	const {
		data,
		isInitialLoading,
		isFetching,
		isFetchingNextPage,
		isError,
		isFetchNextPageError,
		isSuccess,
		hasNextPage,
		fetchNextPage,
		refetch,
	} = useInfiniteQuery({
		queryKey: ["collections", "dialog", searchTerm],
		initialPageParam: 1,
		enabled: isAuthReady && !isDebouncing,
		queryFn: ({ pageParam, signal }) =>
			PixiesetService.listCollectionsPage(pageParam, searchTerm || undefined, signal),
		getNextPageParam: (lastPage) => {
			return lastPage.meta.current_page < lastPage.meta.last_page
				? lastPage.meta.current_page + 1
				: undefined
		},
	})
	const selectedCollectionId = props.selectedCollection
		? String(props.selectedCollection.id)
		: undefined
	const collections = data?.pages.flatMap((page) => page.data) ?? []
	const otherCollections = preselectedCollection
		? collections.filter(
				(collection) => String(collection.id) !== String(preselectedCollection.id),
			)
		: collections
	const containerRef = useRef<HTMLDivElement>(null)
	const loadMoreRef = useRef<HTMLDivElement>(null)
	const isLoading = isDebouncing || isInitialLoading
	const showEmptyState =
		!isLoading &&
		isSuccess &&
		otherCollections.length === 0 &&
		!hasNextPage &&
		(Boolean(searchTerm) || (!preselectedCollection && !props.onAddNew))
	const noResultsMessage = preselectedCollection
		? t("selectCGCollectionDialog.noOtherGalleriesMatch")
		: props.mode === "confirm"
			? t("selectCGCollectionDialog.noGalleriesMatch")
			: t("selectCGCollectionDialog.noCollectionsMatch")

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = 0
		}
	}, [searchTerm])

	useEffect(() => {
		const container = containerRef.current
		const loadMore = loadMoreRef.current
		if (!container || !loadMore || !hasNextPage || isLoading || isError) {
			return
		}

		let isRequestingNextPage = false
		const loadNextPageIfNeeded = () => {
			if (isFetching || isRequestingNextPage) {
				return
			}

			const containerBounds = container.getBoundingClientRect()
			const loadMoreBounds = loadMore.getBoundingClientRect()
			const isNearViewport =
				loadMoreBounds.top <= containerBounds.bottom + 200 &&
				loadMoreBounds.bottom >= containerBounds.top
			if (isNearViewport) {
				isRequestingNextPage = true
				void fetchNextPage()
			}
		}

		const animationFrame = requestAnimationFrame(loadNextPageIfNeeded)
		container.addEventListener("scroll", loadNextPageIfNeeded, { passive: true })

		return () => {
			cancelAnimationFrame(animationFrame)
			container.removeEventListener("scroll", loadNextPageIfNeeded)
		}
	}, [collections.length, fetchNextPage, hasNextPage, isFetching, isLoading, isError, searchTerm])

	const renderAddNewCollection = () => {
		return (
			<button
				type="button"
				data-disable-enter-key="true"
				className="flex h-[90px] flex-row items-center justify-center rounded-md bg-base-700 hover:bg-base-600"
				onClick={props.onAddNew}
			>
				<Icon name="add" size={20} />
				<div className="text-sm font-semibold">
					{t("selectCGCollectionDialog.createCollection")}
				</div>
			</button>
		)
	}
	const renderCollectionCard = (collection: CollectionResponseDTO) => {
		const parsedEventDate = collection.event_date ? parseISO(collection.event_date) : null
		const eventDateLabel =
			parsedEventDate && isValid(parsedEventDate)
				? format(parsedEventDate, "MMM d, yyyy")
				: ""
		const collectionUrl = `${pixiesetURLs.galleries.collections}/${collection.id}`

		return (
			<div
				key={collection.id}
				data-collection-id={String(collection.id)}
				className={clsx(
					"outline outline-2 outline-offset-[-2px]",
					String(collection.id) === selectedCollectionId
						? "outline-accent-600"
						: "outline-transparent",
					`flex h-[90px] flex-row items-start justify-between space-x-2 overflow-hidden rounded-md bg-base-700 p-3 hover:bg-base-600`,
				)}
				onClick={() => {
					if (props.mode === "confirm") {
						props.onSelect(collection)
					} else if (props.mode === "select") {
						popDialog()
						props.onSelect(collection)
					}
				}}
			>
				<div className="flex h-full flex-col justify-between overflow-hidden">
					<div className="flex flex-col space-y-1 overflow-hidden">
						<div className="min-w-0 flex-1 truncate text-left text-xs font-medium">
							{collection.name}
						</div>
						<div className="text-left text-[11px] leading-[13px] text-base-400">{`${collection.photo_count} ${
							collection.photo_count > 1 ? "photos" : "photo"
						}`}</div>
						{eventDateLabel ? (
							<div className="text-left text-[11px] leading-[13px] text-base-400">
								{eventDateLabel}
							</div>
						) : null}
					</div>

					<button
						type="button"
						aria-label={t("selectCGCollectionDialog.ariaViewCollection", {
							name: collection.name,
						})}
						className="w-fit text-[11px] leading-[13px] text-base-400 hover:text-base-100 dark:text-base-400 dark:hover:text-base-100"
						onClick={(e) => {
							e.stopPropagation()
							window.open(collectionUrl, "_blank", "noopener,noreferrer")
						}}
					>
						<div className="flex flex-row space-x-1">
							<Icon
								name="open_in_new"
								size={12}
								className="-ml-px mt-px"
								aria-hidden="true"
							/>
							<span>{t("selectCGCollectionDialog.view")}</span>
						</div>
					</button>
				</div>
				<img
					src={formattedPSImagePathToUrl(collection.coverPhoto)}
					className="aspect-square h-full rounded-md object-cover"
				/>
			</div>
		)
	}
	return (
		<div
			ref={containerRef}
			className="flex size-full min-h-0 flex-1 flex-col space-y-4 overflow-y-auto"
		>
			{preselectedCollection && (
				<div className="flex flex-col space-y-2">
					<div className="text-sm font-semibold">
						{t("selectCGCollectionDialog.selectedCollection")}
					</div>
					<div className="grid h-fit grid-cols-3 gap-3">
						{renderCollectionCard(preselectedCollection)}
					</div>
				</div>
			)}
			{(!isLoading || props.onAddNew) && (
				<div className="flex flex-col space-y-2">
					{preselectedCollection && (
						<div className="text-sm font-semibold">
							{t("selectCGCollectionDialog.otherCollections")}
						</div>
					)}
					<div className="grid h-fit grid-cols-3 gap-3">
						{props.onAddNew && renderAddNewCollection()}
						{!isLoading &&
							otherCollections.map((collection: CollectionResponseDTO) =>
								renderCollectionCard(collection),
							)}
					</div>
				</div>
			)}
			{showEmptyState && (
				<div className="flex size-full flex-1 items-center justify-center text-center">
					<div className="text-sm text-base-400">
						{searchTerm ? noResultsMessage : t("selectCGCollectionDialog.noGalleries")}
					</div>
				</div>
			)}
			{isLoading && (
				<div className="flex size-full flex-1 items-center justify-center">
					<Spinner size={20} />
				</div>
			)}
			{!isLoading && isError && (
				<div
					role="alert"
					className="flex flex-col items-center gap-2 py-4 text-sm text-base-400"
				>
					<span>{t("selectCGCollectionDialog.failedToLoad")}</span>
					<Button
						asChild
						trackingLabel="retry_collection_search"
						aria-label={t("selectCGCollectionDialog.retry")}
						variant="default"
						size="sm"
						disabled={isFetching}
						onClick={() => {
							void (isFetchNextPageError ? fetchNextPage() : refetch())
						}}
					>
						<button type="button" data-disable-enter-key="true" disabled={isFetching}>
							{t("selectCGCollectionDialog.retry")}
						</button>
					</Button>
				</div>
			)}
			{!isLoading && !isError && hasNextPage && (
				<div ref={loadMoreRef} className="h-1 w-full" />
			)}
			{isFetchingNextPage && (
				<div className="flex w-full items-center justify-center pb-1">
					<Spinner size={16} />
				</div>
			)}
		</div>
	)
}
